import { Button, Col, Form, Modal, ModalProps, Row } from 'antd';
import React, { useState } from 'react';
import InvoiceCreateSteps from './InvoiceCreateSteps';
import { Group } from '@/components/_common';
import { InvoiceGeneralFormValues } from '@/core/domain/invoice/invoice/Invoice.types';
import { InvoiceType } from '@/core/domain/invoice/invoices/invoices.enums';
import {
  useInvoiceCreateNFc,
  useInvoiceCreateNFe,
  useInvoiceCreateNFs,
} from '@/core/domain/invoice/invoices/invoices.hooks';
import { DateTimeFormat } from '@/utils/moment';
import { useCustomerGeneralMutation } from '@/core/domain/people/Customers/Customers.hooks';
import { InvoiceData } from '@/core/domain/invoice/invoices/invoices.types';
import { useHistory } from 'react-router-dom';

interface Props extends ModalProps {
  open: boolean;
  onCancel: VoidFunction;
}

const InvoiceCreateModal: React.FC<Props> = (props) => {
  const [form] = Form.useForm<InvoiceGeneralFormValues>();
  const history = useHistory();

  const [current, setCurrent] = useState(0);

  const invoiceCreateNFC = useInvoiceCreateNFc();
  const invoiceCreateNFE = useInvoiceCreateNFe();
  const invoiceCreateNFS = useInvoiceCreateNFs();

  const customerGeneral = useCustomerGeneralMutation();

  async function onSubmit() {
    try {
      const { recipient, ...formValues } = await form.validateFields();

      let result: InvoiceData | undefined;
      let isIrpjCsllRetention: boolean | undefined;

      if (recipient?.customerId) {
        const customerData = await customerGeneral.mutateAsync(
          recipient?.customerId,
        );

        isIrpjCsllRetention = customerData?.retentionIRPJorCSLL;
      }

      const objSimple = {
        ...formValues,
        ...recipient,
        deliveryPrevisionDate:
          formValues.deliveryPrevisionDate?.format('YYYY-MM-DD'),
        isIrpjCsllRetention,
        invoiceItem: [],
        publicAgency: recipient?.publicAgency === 'true',
        simpleNational: formValues.simpleNational
          ? Boolean(formValues.simpleNational)
          : undefined,
        culturalIncentivator: formValues.culturalIncentivator
          ? Boolean(formValues.culturalIncentivator)
          : undefined,
      };

      const objComplementary = {
        ...objSimple,
        faceToFace: formValues.faceToFace
          ? Boolean(formValues.faceToFace)
          : undefined,
        finalConsumer: formValues.finalConsumer
          ? Boolean(formValues.finalConsumer)
          : undefined,
        intermediaryIndication: formValues.intermediaryIndication
          ? Number(Boolean(formValues.intermediaryIndication))
          : undefined,
      };

      if (formValues.invoiceType === InvoiceType.NFC) {
        result = await invoiceCreateNFC.mutateAsync({
          ...objComplementary,
          emissionDate: formValues.emissionDate?.format(DateTimeFormat),
        });
      } else if (formValues.invoiceType === InvoiceType.NFE) {
        result = await invoiceCreateNFE.mutateAsync({
          ...objComplementary,
          departureDate: formValues.departureDate?.format(DateTimeFormat),
        });
      } else if (formValues.invoiceType === InvoiceType.NFS) {
        const recipientType =
          recipient.supplierId || recipient.insurerId || recipient.customerId
            ? recipient.recipientType
            : undefined;

        result = await invoiceCreateNFS.mutateAsync({
          ...objSimple,
          recipientType,
        });
      }

      if (result) {
        history.push(
          `/app/fiscal/invoice/${result.invoiceType}/${result.id}/datas`,
        );
      }
    } catch {}
  }

  async function onClose() {
    setCurrent(0);
    form.resetFields();
    props.onCancel();
  }

  function onPrev() {
    setCurrent(current - 1);
  }

  async function onNext() {
    try {
      if (current === 0) {
        await form.validateFields(['companyId', 'invoiceType']);
      } else if (current === 1) {
        await form.validateFields([
          'situation',
          'integrationId',
          'serie',
          'taxationNature',
          'numberRps',
          'serviceItemList',
          'natureOfOperation',
          'emissionGoal',
          'documentType',
          'operationDestination',
          'departureDate',
          'emissionType',
          'faceToFace',
        ]);
      } else if (current === 2) {
        const recipientType = form.getFieldValue([
          'recipient',
          'recipientType',
        ]);

        if (recipientType === 'CUSTOMER') {
          await form.validateFields([
            ['recipient', 'cpfCnpj'],
            ['recipient', 'customer'],
            ['recipient', 'customerId'],
          ]);
        } else if (recipientType === 'SUPPLIER') {
          await form.validateFields([
            ['recipient', 'cpfCnpj'],
            ['recipient', 'supplier'],
            ['recipient', 'supplierId'],
          ]);
        } else {
          await form.validateFields([
            ['recipient', 'cpfCnpj'],
            ['recipient', 'insurer'],
            ['recipient', 'insurerId'],
          ]);
        }
      }

      if (current < 2) {
        setCurrent(current + 1);
      } else {
        await onSubmit();
      }
    } catch {}
  }

  function modalFooter() {
    return (
      <Row gutter={[16, 16]} justify="space-between" align="middle">
        <Col>
          <Button onClick={onClose}>Cancelar</Button>
        </Col>

        <Col>
          <Group gap={16}>
            {current > 0 && <Button onClick={onPrev}>Voltar</Button>}

            {current < 2 && (
              <Button type="primary" ghost onClick={onNext}>
                Continuar
              </Button>
            )}

            {current === 2 && (
              <Button
                type="primary"
                onClick={onNext}
                loading={
                  invoiceCreateNFC.isLoading ||
                  invoiceCreateNFE.isLoading ||
                  invoiceCreateNFS.isLoading
                }
              >
                Cadastrar
              </Button>
            )}
          </Group>
        </Col>
      </Row>
    );
  }

  return (
    <>
      <Modal
        {...props}
        onCancel={onClose}
        title="Cadastro de Nota Fiscal"
        footer={modalFooter()}
        width={current < 1 ? 600 : 900}
        destroyOnClose
        maskClosable={false}
      >
        <Form form={form}>
          <InvoiceCreateSteps current={current} onStepChange={setCurrent} />
        </Form>
      </Modal>
    </>
  );
};

export default InvoiceCreateModal;
